import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { TimerStatus } from '@flm9/scoring';
import { bleAvailable, SgTimerSource, type RawEventLogEntry } from './sg';

interface TimerCtx {
  source: SgTimerSource;
  status: TimerStatus;
  rawLog: readonly RawEventLogEntry[];
  bleSupported: boolean;
  isConnected: boolean;
  /** True between the user's tap and the GATT chooser closing. */
  pairing: boolean;
  /** Last error from connect/start/stop, sticky until the next attempt. */
  error: string | null;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  /**
   * Round assignment the current run belongs to (set when a run starts on
   * a shooter's Score screen). The source is a singleton, so after "📣
   * Aufrufen" its status still holds the PREVIOUS shooter's stopped run —
   * consumers read the status through `useTimerRun(assignmentId)`, which
   * hides a run that isn't theirs.
   */
  runOwner: string | null;
  claimRun(assignmentId: string): void;
}

const TimerContext = createContext<TimerCtx | null>(null);

const PAR_DELAY_LS_KEY = 'flm9.referee.timer.parStartDelayDS';
const PAR_TIME_LS_KEY = 'flm9.referee.timer.parTimeLimitDS';

function readNumberFromStorage(key: string, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Owns the singleton SgTimerSource for the lifetime of the PWA tab.
 * Pairing requires a user gesture, so we never auto-connect on mount —
 * the chip in the header / debug screen kicks it off explicitly. PAR
 * defaults are persisted across reloads so a club can configure once.
 */
export function TimerProvider({ children }: { children: ReactNode }) {
  const sourceRef = useRef<SgTimerSource | null>(null);
  if (sourceRef.current == null) {
    sourceRef.current = new SgTimerSource({
      parStartDelayDS: readNumberFromStorage(PAR_DELAY_LS_KEY, 30),
      parTimeLimitDS: readNumberFromStorage(PAR_TIME_LS_KEY, 0),
    });
  }
  const source = sourceRef.current;

  const [status, setStatus] = useState<TimerStatus>(() => source.status());
  const [rawLog, setRawLog] = useState<readonly RawEventLogEntry[]>(() => source.rawEventLog());
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runOwner, setRunOwner] = useState<string | null>(null);
  const claimRun = useCallback((assignmentId: string) => setRunOwner(assignmentId), []);

  useEffect(() => {
    const offStatus = source.on(setStatus);
    const offLog = source.onRawLog(setRawLog);
    return () => {
      offStatus();
      offLog();
    };
  }, [source]);

  // Reload recovery: silently re-adopt a previously-granted timer (Chromium
  // getDevices()) so a full PWA reload doesn't force a re-pair. No chooser,
  // no user gesture; no-ops on unsupported browsers.
  useEffect(() => {
    void source.tryAdoptKnownDevice();
  }, [source]);

  // Foreground recovery: mobile PWAs commonly have their BLE link dropped
  // while backgrounded. When the tab becomes visible again, kick an
  // immediate reconnect if the link is down.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') source.maybeReconnectNow();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [source]);

  const connect = useCallback(async () => {
    setError(null);
    setPairing(true);
    try {
      await source.connect();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'connect_failed');
      throw err;
    } finally {
      setPairing(false);
    }
  }, [source]);

  const disconnect = useCallback(async () => {
    try {
      await source.disconnect();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'disconnect_failed');
    }
  }, [source]);

  const ctx = useMemo<TimerCtx>(
    () => ({
      source,
      status,
      rawLog,
      bleSupported: bleAvailable(),
      isConnected: source.isConnected(),
      pairing,
      error,
      connect,
      disconnect,
      runOwner,
      claimRun,
    }),
    [source, status, rawLog, pairing, error, connect, disconnect, runOwner, claimRun],
  );

  return <TimerContext.Provider value={ctx}>{children}</TimerContext.Provider>;
}

export function useTimer(): TimerCtx {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer must be used within <TimerProvider>');
  return ctx;
}

/** Status with the run data dropped — device/connection metadata stays. */
function withoutRun(status: TimerStatus): TimerStatus {
  const { lastSessId: _drop, ...rest } = status;
  return { ...rest, state: 'idle', startedAt: null, shots: [] };
}

/**
 * `useTimer()` scoped to one shooter: `status` is the source's status only
 * while the current run belongs to `assignmentId`, else an idle status
 * (no shots, no times). Everything that saves, sends or pairs timer data
 * for a shooter must read it through this hook.
 */
export function useTimerRun(assignmentId: string): TimerCtx & { ownsRun: boolean } {
  const ctx = useTimer();
  const ownsRun = ctx.runOwner === assignmentId;
  const status = useMemo(
    () => (ownsRun ? ctx.status : withoutRun(ctx.status)),
    [ownsRun, ctx.status],
  );
  return { ...ctx, status, ownsRun };
}

/**
 * A run armed on the timer itself (its own start button) while this
 * screen is open belongs to `assignmentId` — the shooter whose Score
 * screen is open, or that the timer screen was opened from. Only a fresh
 * transition into `armed` counts: a run already going when the screen
 * mounts keeps its owner (or stays unowned).
 */
export function useClaimArmedRun(assignmentId: string | null): void {
  const { status, runOwner, claimRun } = useTimer();
  const rawState = status.state;
  const prevRef = useRef(rawState);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = rawState;
    if (assignmentId == null) return;
    if (rawState === 'armed' && prev !== 'armed' && runOwner !== assignmentId) {
      claimRun(assignmentId);
    }
  }, [rawState, runOwner, claimRun, assignmentId]);
}

export function persistPar(startDelayDS: number, timeLimitDS: number): void {
  try {
    window.localStorage.setItem(PAR_DELAY_LS_KEY, String(startDelayDS));
    window.localStorage.setItem(PAR_TIME_LS_KEY, String(timeLimitDS));
  } catch {
    // Storage may be unavailable (private mode) — non-fatal.
  }
}
